import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";

import { AuthProvider, useAuth } from "./context/auth";
import { ThemeProvider } from "./context/theme";
import { LocaleProvider } from "./context/locale";
import { ToastProvider } from "./context/toast";
import { Shell } from "./components/layout/shell";
import { SignIn } from "./pages/sign-in";
import { Dashboard } from "./pages/dashboard";
import { ReviewQueue } from "./pages/review-queue";
import { Players } from "./pages/players";
import { PlayerDetail } from "./pages/player-detail";
import { Bets } from "./pages/bets";
import { Draws } from "./pages/draws";
import { Games } from "./pages/games";
import { Referrals } from "./pages/referrals";
import { Reports } from "./pages/reports";
import { AuditLog } from "./pages/audit-log";
import { TestPlayers } from "./pages/test-players";
import { Settings } from "./pages/settings";
import { Notifications } from "./pages/notifications";
import { Admins } from "./pages/admins";
import { Permissions } from "./pages/permissions";
import { Sessions } from "./pages/sessions";
import { Ledger } from "./pages/ledger";

/**
 * Everything behind this needs a session. The attempted path travels with the
 * redirect so a session that expires mid-task returns the operator to where they
 * were rather than dumping them on the dashboard.
 */
function Gate() {
  const { admin } = useAuth();
  const location = useLocation();
  if (admin) return <Shell />;
  const from = location.pathname + location.search;
  return (
    <Navigate
      to="/signin"
      replace
      state={{ from: from === "/" ? undefined : from }}
    />
  );
}

function SignInRoute() {
  const { admin } = useAuth();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from;
  return admin ? <Navigate to={from ?? "/"} replace /> : <SignIn />;
}

export function App() {
  return (
    <ThemeProvider>
      <LocaleProvider>
        <ToastProvider>
          <BrowserRouter>
            <AuthProvider>
              <Routes>
                <Route path="/signin" element={<SignInRoute />} />
                <Route element={<Gate />}>
                  <Route index element={<Dashboard />} />
                  <Route path="review" element={<ReviewQueue />} />
                  <Route path="players" element={<Players />} />
                  <Route path="players/:id" element={<PlayerDetail />} />
                  <Route path="bets" element={<Bets />} />
                  <Route path="draws" element={<Draws />} />
                  <Route path="games" element={<Games />} />
                  <Route path="referrals" element={<Referrals />} />
                  <Route path="reports" element={<Reports />} />
                  <Route path="audit" element={<AuditLog />} />
                  <Route path="test-players" element={<TestPlayers />} />
                  <Route path="admins" element={<Admins />} />
                  <Route path="permissions" element={<Permissions />} />
                  <Route path="sessions" element={<Sessions />} />
                  <Route path="ledger" element={<Ledger />} />
                  <Route path="notifications" element={<Notifications />} />
                  <Route path="settings" element={<Settings />} />
                </Route>
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </AuthProvider>
          </BrowserRouter>
        </ToastProvider>
      </LocaleProvider>
    </ThemeProvider>
  );
}
